// IdentifyStep — identify-first sign-in, shared by every surface.
//
// Browsing a site needs no account. When something does (a checkout, an
// `auth_form` in `mode: "identify"`, any block that asks the host to render
// it), the visitor types an EMAIL ADDRESS or a MOBILE NUMBER and:
//
//   · nobody holds it  → optional name + "Continue": the host quick-registers
//                        an account (password-less, the project's default
//                        roles), signs them in for this session, and a "set
//                        your password" link is sent to that address. A new
//                        account records BOTH contacts: an email sign-up is
//                        also asked for a mobile number, a mobile sign-up for
//                        an email address (which receives the link);
//   · somebody does    → password + "Log in", or "Forgot password?" (sends a
//                        reset link and says so);
//   · it exists with no password yet → offer to (re)send the set-password link.
//
// The component never calls an API: it talks to the host through
// `IdentifyAdapter`, which the host puts on its `AuthMenuContext` adapter
// (`AuthMenuAdapter.identify`) — the site viewer wires it to
// `/site-auth/identify | quick-register | login | forgot-password`. With no
// adapter (designer canvas / SSR) it renders the first step visual-only.
//
// Every visible string is a prop with a default (`IdentifyStepLabels`), so an
// author can re-word it per block; nothing in here is specific to a shop.
//
// Deliberately imports nothing from './renderers' (which imports this file).
import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'

export interface IdentifyResult {
  exists: boolean
  kind: 'email' | 'phone'
  /** Whether a NEW account may be created from this identifier here. */
  can_register: boolean
  /** Whether the existing account has a password (false = never set one). */
  has_password: boolean
  /** Whether registering also needs a mobile number (false only where the
   *  application's registry cannot hold one). */
  phone_required?: boolean
}

/** The contact a new account records beside the identifier it was found by. */
export interface QuickRegisterContact {
  /** Required when the identifier is a mobile number — the link is mailed here. */
  email?: string
  /** Required when the identifier is an email (unless `phone_required` is false). */
  phone?: string
}

export interface QuickRegisterResult {
  /** Whether the set-password link was actually sent (email, or the project's SMS channel). */
  setPasswordSent: boolean
  channel?: 'email' | 'sms' | null
  /** The application charges an access fee; no session was issued. */
  paymentRequired?: boolean
}

/** What the host provides. Each call resolves once the host has stored the
 *  session (quickRegister / login), or rejects with an Error whose message is
 *  fit to show the visitor. */
export interface IdentifyAdapter {
  identify: (identifier: string) => Promise<IdentifyResult>
  quickRegister: (identifier: string, name?: string, contact?: QuickRegisterContact) => Promise<QuickRegisterResult>
  login: (identifier: string, password: string) => Promise<void>
  /** Ask for a reset / set-password link. Never reveals more than identify did. */
  forgotPassword: (identifier: string) => Promise<void>
}

export interface IdentifySignedIn {
  /** True when this step just created the account. */
  created: boolean
  setPasswordSent?: boolean
  channel?: 'email' | 'sms' | null
}

export interface IdentifyStepLabels {
  heading: string
  intro: string
  identifier: string
  identifierPlaceholder: string
  next: string
  newIntro: string
  name: string
  email: string
  emailHint: string
  emailRequired: string
  mobile: string
  mobileRequired: string
  continueLabel: string
  passwordIntro: string
  password: string
  login: string
  forgot: string
  forgotSent: string
  noPasswordIntro: string
  sendLink: string
  linkSent: string
  cannotRegister: string
  change: string
  createdSent: string
  createdNotSent: string
  paymentRequired: string
}

export const IDENTIFY_DEFAULT_LABELS: IdentifyStepLabels = {
  heading: 'Your details',
  intro: 'Enter your email address or mobile number to continue.',
  identifier: 'Email or mobile number',
  identifierPlaceholder: 'Email address or mobile number',
  next: 'Next',
  newIntro: "You're new here — we'll create your account.",
  name: 'Your name (optional)',
  email: 'Email address',
  emailHint: "We'll email you a link to set your password.",
  emailRequired: 'Enter your email address so we can send you a link to set your password.',
  mobile: 'Mobile number',
  mobileRequired: 'Enter your mobile number.',
  continueLabel: 'Continue',
  passwordIntro: 'Welcome back — enter your password.',
  password: 'Password',
  login: 'Log in',
  forgot: 'Forgot password?',
  forgotSent: 'If we can reach you there, a link to reset your password is on its way.',
  noPasswordIntro: "This account doesn't have a password yet. We can send you a link to set one.",
  sendLink: 'Send me a link',
  linkSent: 'A link to set your password is on its way.',
  cannotRegister: 'No account was found for this, and new accounts cannot be created here.',
  change: 'Change',
  createdSent: "You're signed in. We've sent you a link to set a password for next time.",
  createdNotSent: "You're signed in. You can set a password later from My account.",
  paymentRequired: 'This application charges an access fee — finish signing up on the sign-in page.',
}

export type IdentifyStepName = 'identify' | 'new' | 'password' | 'no-password' | 'sent' | 'done' | 'closed'

export interface IdentifyStepProps {
  adapter?: IdentifyAdapter | null
  onSignedIn?: (info: IdentifySignedIn) => void
  labels?: Partial<IdentifyStepLabels>
  /** Render without the outer card (when the host already frames it). */
  bare?: boolean
  /** Hosts that already know the answer (and tests) may start further in. */
  initialStep?: IdentifyStepName
  initialIdentifier?: string
  /** Rendered under the form (e.g. a "Sign in with Twasta" button). */
  footer?: ReactNode
}

const box: CSSProperties = {
  display: 'grid', gap: 12, maxWidth: 420, width: '100%', margin: '0 auto', padding: 20,
  border: '1px solid var(--pg-border)', borderRadius: 'var(--pg-radius, 12px)',
  background: 'var(--pg-surface)', color: 'var(--pg-text)', boxSizing: 'border-box',
}
const inp: CSSProperties = {
  padding: '10px 12px', border: '1px solid var(--pg-border)', borderRadius: 8,
  fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none',
  background: 'var(--pg-bg)', color: 'var(--pg-text)', fontFamily: 'inherit',
}
const lbl: CSSProperties = {
  display: 'grid', gap: 4, fontSize: 12, fontWeight: 600,
  color: 'var(--pg-muted, var(--pg-text-secondary))',
}
const btn: CSSProperties = {
  padding: '10px 16px', fontSize: 14, fontWeight: 600, borderRadius: 'var(--pg-button-radius, 8px)',
  border: 'none', background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)',
  cursor: 'pointer', width: '100%', fontFamily: 'inherit',
}
const linkBtn: CSSProperties = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13,
  color: 'var(--pg-link, var(--pg-primary))', textDecoration: 'underline', fontFamily: 'inherit',
}
const note: CSSProperties = { margin: 0, fontSize: 13, color: 'var(--pg-muted, var(--pg-text-secondary))' }

/** The identify step asks for an email OR a mobile number; anything without
 *  an "@" is the number (the server decides definitively). */
function isPhoneIdentifier(kind: 'email' | 'phone' | null, identifier: string): boolean {
  return kind ? kind === 'phone' : !identifier.includes('@')
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function messageOf(err: unknown, fallback = 'Something went wrong — please try again.'): string {
  return err instanceof Error && err.message ? err.message : fallback
}

export function IdentifyStep({
  adapter, onSignedIn, labels, bare, initialStep, initialIdentifier, footer,
}: IdentifyStepProps) {
  const L = { ...IDENTIFY_DEFAULT_LABELS, ...(labels || {}) }
  const [step, setStep] = useState<IdentifyStepName>(initialStep || 'identify')
  const [identifier, setIdentifier] = useState(initialIdentifier || '')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [kind, setKind] = useState<'email' | 'phone' | null>(null)
  const [phoneRequired, setPhoneRequired] = useState(true)
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (fn: () => Promise<void>) => {
    if (!adapter || busy) return
    setError(null); setBusy(true)
    try { await fn() } catch (err) { setError(messageOf(err)) } finally { setBusy(false) }
  }

  const doIdentify = (e: FormEvent) => {
    e.preventDefault()
    const id = identifier.trim()
    if (!id) { setError(L.intro); return }
    void run(async () => {
      const r = await adapter!.identify(id)
      setKind(r.kind || null)
      setPhoneRequired(r.phone_required !== false)
      setInfo(null)
      if (!r.exists) setStep(r.can_register ? 'new' : 'identify')
      else setStep(r.has_password ? 'password' : 'no-password')
      if (!r.exists && !r.can_register) setError(L.cannotRegister)
    })
  }

  const phone = isPhoneIdentifier(kind, identifier.trim())
  // A new account records both contacts: ask for whichever was not typed.
  const askMobile = !phone && phoneRequired

  const doRegister = (e: FormEvent) => {
    e.preventDefault()
    const mail = email.trim()
    const mob = mobile.trim()
    if (phone && !EMAIL_RE.test(mail)) { setError(L.emailRequired); return }
    if (askMobile && !mob) { setError(L.mobileRequired); return }
    void run(async () => {
      const r = await adapter!.quickRegister(
        identifier.trim(), name.trim() || undefined,
        phone ? { email: mail } : askMobile ? { phone: mob } : undefined)
      if (r.paymentRequired) { setError(L.paymentRequired); return }
      setInfo(r.setPasswordSent ? L.createdSent : L.createdNotSent)
      setStep('done')
      onSignedIn?.({ created: true, setPasswordSent: r.setPasswordSent, channel: r.channel ?? null })
    })
  }

  const doLogin = (e: FormEvent) => {
    e.preventDefault()
    if (!password) return
    void run(async () => {
      await adapter!.login(identifier.trim(), password)
      setStep('done')
      onSignedIn?.({ created: false })
    })
  }

  const doForgot = (sentMessage: string) => {
    void run(async () => {
      await adapter!.forgotPassword(identifier.trim())
      setInfo(sentMessage)
      setStep('sent')
    })
  }

  const back = () => { setStep('identify'); setPassword(''); setKind(null); setError(null); setInfo(null) }

  const identifierRow = step !== 'identify' && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
      <span data-identify-value style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{identifier}</span>
      {step !== 'done' && <button type="button" style={linkBtn} onClick={back}>{L.change}</button>}
    </div>
  )
  const busyStyle = (b: CSSProperties): CSSProperties =>
    ({ ...b, opacity: busy ? 0.7 : 1, cursor: busy ? 'wait' : b.cursor })

  let body: ReactNode
  if (step === 'identify') {
    body = (
      <form onSubmit={doIdentify} style={{ display: 'grid', gap: 12 }} data-identify-step="identify">
        <p style={note}>{L.intro}</p>
        <label style={lbl}>
          {L.identifier}
          <input style={inp} value={identifier} autoComplete="username" inputMode="email"
            placeholder={L.identifierPlaceholder} onChange={(e) => setIdentifier(e.currentTarget.value)} />
        </label>
        <button type="submit" disabled={busy || !adapter} style={busyStyle(btn)}>{L.next}</button>
      </form>
    )
  } else if (step === 'new') {
    body = (
      <form onSubmit={doRegister} style={{ display: 'grid', gap: 12 }} data-identify-step="new">
        {identifierRow}
        <p style={note}>{L.newIntro}</p>
        <label style={lbl}>
          {L.name}
          <input style={inp} value={name} autoComplete="name" onChange={(e) => setName(e.currentTarget.value)} />
        </label>
        {phone && (
          <label style={lbl}>
            {L.email}
            <input style={inp} type="email" required value={email} autoComplete="email" inputMode="email"
              data-identify-email onChange={(e) => setEmail(e.currentTarget.value)} />
            <span style={{ fontWeight: 400 }}>{L.emailHint}</span>
          </label>
        )}
        {askMobile && (
          <label style={lbl}>
            {L.mobile}
            <input style={inp} type="tel" required value={mobile} autoComplete="tel" inputMode="tel"
              data-identify-mobile onChange={(e) => setMobile(e.currentTarget.value)} />
          </label>
        )}
        <button type="submit" disabled={busy} style={busyStyle(btn)}>{L.continueLabel}</button>
      </form>
    )
  } else if (step === 'password') {
    body = (
      <form onSubmit={doLogin} style={{ display: 'grid', gap: 12 }} data-identify-step="password">
        {identifierRow}
        <p style={note}>{L.passwordIntro}</p>
        <label style={lbl}>
          {L.password}
          <input style={inp} type="password" value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.currentTarget.value)} />
        </label>
        <button type="submit" disabled={busy} style={busyStyle(btn)}>{L.login}</button>
        <button type="button" style={{ ...linkBtn, justifySelf: 'center' }} disabled={busy}
          onClick={() => doForgot(L.forgotSent)}>{L.forgot}</button>
      </form>
    )
  } else if (step === 'no-password') {
    body = (
      <div style={{ display: 'grid', gap: 12 }} data-identify-step="no-password">
        {identifierRow}
        <p style={note}>{L.noPasswordIntro}</p>
        <button type="button" disabled={busy} style={busyStyle(btn)} onClick={() => doForgot(L.linkSent)}>
          {L.sendLink}
        </button>
      </div>
    )
  } else {
    // 'sent' | 'done' | 'closed'
    body = (
      <div style={{ display: 'grid', gap: 12 }} data-identify-step={step}>
        {identifierRow}
        {info && <p role="status" style={{ ...note, color: 'var(--pg-text)' }}>{info}</p>}
        {step === 'sent' && <button type="button" style={{ ...linkBtn, justifySelf: 'start' }} onClick={back}>{L.change}</button>}
      </div>
    )
  }

  const inner = (
    <>
      {L.heading && (
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--pg-heading, var(--pg-text))' }}>
          {L.heading}
        </h3>
      )}
      {body}
      {error && <div role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontSize: 13 }}>{error}</div>}
      {footer}
    </>
  )
  return bare
    ? <div style={{ display: 'grid', gap: 12 }} data-identify-root>{inner}</div>
    : <div style={box} data-identify-root>{inner}</div>
}

/** The `renderIdentify` a host hands to blocks that need an account (e.g.
 *  `cart_checkout` via `PageDataBlocksApi.renderIdentify`). */
export function renderIdentifyFor(adapter: IdentifyAdapter | null | undefined) {
  return function renderIdentify(opts: {
    onSignedIn?: (info: IdentifySignedIn) => void
    labels?: Partial<IdentifyStepLabels>
    /** The refusal code that made the block ask (e.g. SIGN_IN_REQUIRED). */
    reason?: string
    /** The server's wording for it — shown as the step's intro. */
    message?: string
    /** No card of its own — the block frames it. */
    bare?: boolean
  } = {}): ReactNode {
    const labels = { ...(opts.message ? { intro: opts.message } : {}), ...(opts.labels || {}) }
    return <IdentifyStep adapter={adapter} onSignedIn={opts.onSignedIn} labels={labels} bare={opts.bare} />
  }
}

// ─── Set password (the link sent by quick-register / forgot password) ──────

export interface SetPasswordLabels {
  heading: string
  intro: string
  password: string
  confirm: string
  submit: string
  mismatch: string
  tooShort: string
  done: string
}

export const SET_PASSWORD_DEFAULT_LABELS: SetPasswordLabels = {
  heading: 'Set your password',
  intro: 'Choose a password to sign in with next time.',
  password: 'New password',
  confirm: 'Confirm password',
  submit: 'Save password',
  mismatch: 'The two passwords do not match.',
  tooShort: 'Use at least 8 characters.',
  done: 'Your password is set. You can sign in with it from now on.',
}

/** Minimum length — mirrors the registry's own rule (registry_store.set_password). */
export const MIN_PASSWORD_LENGTH = 8

export function SetPasswordPanel({ onSubmit, onDone, labels }: {
  onSubmit?: (newPassword: string) => Promise<void>
  onDone?: () => void
  labels?: Partial<SetPasswordLabels>
}) {
  const L = { ...SET_PASSWORD_DEFAULT_LABELS, ...(labels || {}) }
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!onSubmit || busy) return
    if (pw.length < MIN_PASSWORD_LENGTH) { setError(L.tooShort); return }
    if (pw !== pw2) { setError(L.mismatch); return }
    setError(null); setBusy(true)
    try { await onSubmit(pw); setDone(true); onDone?.() } catch (err) { setError(messageOf(err)) } finally { setBusy(false) }
  }
  return (
    <form onSubmit={submit} style={box} data-set-password>
      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{L.heading}</h3>
      {done ? <p role="status" style={note}>{L.done}</p> : (
        <>
          <p style={note}>{L.intro}</p>
          <label style={lbl}>{L.password}
            <input style={inp} type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.currentTarget.value)} />
          </label>
          <label style={lbl}>{L.confirm}
            <input style={inp} type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.currentTarget.value)} />
          </label>
          <button type="submit" disabled={busy || !onSubmit} style={btn}>{L.submit}</button>
        </>
      )}
      {error && <div role="alert" style={{ color: 'var(--pg-error, #dc2626)', fontSize: 13 }}>{error}</div>}
    </form>
  )
}
